import { useToast } from '../../../context/ToastContext'
import { useRevealClass } from '../../../hooks/useRevealClass'
import AcademyZodiacWheel from './AcademyZodiacWheel'
import { FEATURE_PILLS, STUDENT_AVATARS, TWINKLE_STARS } from './academyData'
import './AcademySection.css'
import { appUrl } from '@astro/config'

function FeaturePillIcon({ pill }) {
  if (pill.icon === 'star') {
    return (
      <svg className="w-2.5 h-2.5 opacity-75" fill="currentColor" viewBox="0 0 24 24">
        <path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
      </svg>
    )
  }
  return pill.iconColor ? <span style={{ color: pill.iconColor }}>{pill.icon}</span> : <span>{pill.icon}</span>
}

export default function AcademySection() {
  const { showToast } = useToast()
  const contentReveal = useRevealClass()

  return (
    <section
      id="learn-astrology"
      className="relative overflow-hidden"
      style={{ background: 'radial-gradient(ellipse 120% 100% at 70% 45%,#C2710C 0%,#92400E 28%,#4A1A05 60%,#1A0803 100%)' }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute" style={{ top: '-10%', left: '-8%', width: '600px', height: '600px', background: 'radial-gradient(circle,rgba(245,158,11,.22) 0%,transparent 65%)', filter: 'blur(80px)' }} />
        <div className="absolute" style={{ bottom: '-15%', right: '5%', width: '500px', height: '500px', background: 'radial-gradient(circle,rgba(161,60,10,.30) 0%,transparent 65%)', filter: 'blur(70px)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: '800px', height: '500px', background: 'radial-gradient(ellipse,rgba(245,158,11,.08) 0%,transparent 60%)', filter: 'blur(40px)' }} />

        {TWINKLE_STARS.map((star, i) => (
          <div
            key={i}
            className="la-star"
            style={{
              top: star.top,
              left: star.left,
              width: `${star.size}px`,
              height: `${star.size}px`,
              background: star.color,
              '--d': star.duration,
              '--dl': star.delay,
            }}
          />
        ))}

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(245,158,11,.3),transparent)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(245,158,11,.2),transparent)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center pt-14 lg:pt-20 pb-10">
          <div
            ref={contentReveal.ref}
            className={`space-y-5 order-2 lg:order-1 la-reveal${contentReveal.visible ? ' la-visible' : ''}`}
            style={{ transitionDelay: '.05s' }}
          >
            <div className="inline-flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center text-base" style={{ background: 'linear-gradient(135deg,#D97706,#F59E0B)', boxShadow: '0 4px 16px rgba(245,158,11,.4)' }}>
                📚
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[.4em]" style={{ color: 'rgba(245,158,11,.85)' }}>
                Raju Maharaj Ji Academy
              </span>
            </div>

            <div className="space-y-1">
              <h2 className="font-royal text-4xl sm:text-[44px] lg:text-[48px] font-normal leading-none tracking-tight text-white">Learn</h2>
              <h2
                className="font-royal text-4xl sm:text-[44px] lg:text-[48px] h-9 md:h-14 font-normal leading-none tracking-tight"
                style={{
                  background: 'linear-gradient(90deg,#FBBF24 0%,#FDE68A 40%,#F59E0B 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Astrology
              </h2>
            </div>

            <p className="text-sm sm:text-[15px] leading-[1.85] max-w-md" style={{ color: 'rgba(255,255,255,.58)' }}>
              Join live online classes and certified courses in Astrology, Palmistry, and Vastu — made simple for beginners and experts
              alike. Learn directly from Maharaj Ji.
            </p>

            <div className="flex flex-wrap gap-2">
              {FEATURE_PILLS.map((pill) => (
                <span className="la-feature-pill" key={pill.label}>
                  <FeaturePillIcon pill={pill} /> {pill.label}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-1">
              <a
                href={appUrl('education', "classes")}
                className="enroll-btn-la text-[11px] uppercase tracking-[.22em] px-7 py-3 text-sm"
              >
                Start Learning
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {STUDENT_AVATARS.map((a) => (
                    <div
                      key={a.letter}
                      className="w-8 h-8 rounded-full border-2 border-amber-500/40 flex items-center justify-center text-[9px] font-bold"
                      style={{ background: a.gradient, color: '#FDE68A' }}
                    >
                      {a.letter}
                    </div>
                  ))}
                  <div className="w-8 h-8 rounded-full border-2 border-amber-500/40 flex items-center justify-center text-[9px] font-bold" style={{ background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.6)' }}>
                    +
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-white">500+</p>
                  <p className="text-[9px] uppercase tracking-widest" style={{ color: 'rgba(255,255,255,.4)' }}>
                    Students Guided
                  </p>
                </div>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full la-float-badge" style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.12)' }}>
              <div className="relative shrink-0 w-3 h-3">
                <div className="live-ring absolute inset-0 rounded-full" style={{ background: 'rgba(239,68,68,.45)' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: '#ef4444', boxShadow: '0 0 8px rgba(239,68,68,.8)' }} />
              </div>
              <span className="text-[11px] font-semibold text-white">Live Class in Progress</span>
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,.2)', color: '#FCA5A5' }}>
                NOW
              </span>
            </div>
          </div>

          <AcademyZodiacWheel />
        </div>
      </div>
    </section>
  )
}
